import { z } from 'zod';
import {
  EmploymentStatusSchema,
  EmployeeEnrollmentStatusSchema,
  WorkforceTypeSchema,
  AttendanceProfileSchema,
} from './employee.schema.js';
import { ErpStatusFilterShape } from './erpStatusFilter.schema.js';
import { WorkforceGroupQuerySchema } from '../filters/workforceGroup.js';

/**
 * Bulk worker operations — workers are edited in GROUPS and are categorized by
 * POSITION. A "group" here is an AD-HOC selection, NOT a persistent crew/gang
 * entity: either an explicit list of employeeIds OR a filter (camp/worksite,
 * position/designation, employment status, enrollment status, free-text
 * search). The selection is resolved server-side and ANDed with the actor's
 * permission scope on every bulk mutation.
 */

/**
 * Filter half of a worker selection. Every field is optional and ANDed; an
 * empty object is rejected by WorkerSelectionSchema's refine (a selection must
 * carry employeeIds OR a non-empty filter so a bulk op never targets "everyone"
 * by accident).
 *
 * ⚠️ THIS SHAPE MUST COVER EVERY FILTER THE ROSTER CAN BE NARROWED BY.
 *
 * It is not merely a convenience subset. "Select all matching the filter" on
 * the employee list posts a selection built from these fields, and the server
 * resolves it against the database — so a filter the roster applies but this
 * object cannot express is a filter that SILENTLY DISAPPEARS between what the
 * user saw and what the mutation writes.
 *
 * That is not hypothetical: `departmentId`, `legalEntityId`, `supplierId` and
 * `workforceType` were all missing here while the list queried on them. Filter
 * the roster to one department, "select all N matching", confirm a bulk
 * terminate, and the server received a filter with none of that in it and
 * resolved every employee in the actor's scope instead of the N on screen. The
 * refine above did not catch it because the client sends `{ search: '' }` to
 * satisfy it, and `''` is falsy everywhere it is then tested.
 *
 * So: if you add a filter to the employee list query, add it here, to
 * `RosterFilters`/`toSelectionFilter` in apps/web, and to `resolveSelection` in
 * the API — all four, or the roster and the bulk action disagree again.
 */
export const WorkerSelectionFilterSchema = z.object({
  /** Stable camp home (WorksiteAssignment to a CAMP-type worksite). */
  campId: z.string().optional(),
  /** Any worksite assignment (camp OR site). */
  worksiteId: z.string().optional(),
  /** Position / trade classification — the existing Employee.designation FK. */
  designationId: z.string().optional(),
  /** Org unit — Employee.departmentId. */
  departmentId: z.string().optional(),
  /** Company branch / legal entity — Employee.legalEntityId. */
  legalEntityId: z.string().optional(),
  /** ERP payroll — Employee.erpPayrollNumber, exact text (`003` is not `3`). */
  erpPayrollNumber: z.string().trim().max(10).optional(),
  /** Labour supplier / agency — Employee.supplierId. */
  supplierId: z.string().optional(),
  /**
   * The workforce group — Workers · Staff · Remote, by the one rule the roster
   * applies (`workforceGroupWhere`). Here because a narrowing the LIST applies
   * that this filter cannot express vanishes the moment somebody escalates to
   * "select all matching".
   */
  group: WorkforceGroupQuerySchema.optional(),
  /** The older spellings of `group` — read as the group, exactly as the list reads them. */
  workforceType: WorkforceTypeSchema.optional(),
  attendanceProfile: AttendanceProfileSchema.optional(),
  /** Employee.nationality, exactly as stored — the roster's nationality filter. */
  nationality: z.string().max(80).optional(),
  employmentStatus: EmploymentStatusSchema.optional(),
  /** The roster's status filter: any-of statuses, ERP code, set by HR. */
  ...ErpStatusFilterShape,
  enrollmentStatus: EmployeeEnrollmentStatusSchema.optional(),
  search: z.string().optional(),
});
export type WorkerSelectionFilter = z.infer<typeof WorkerSelectionFilterSchema>;

/**
 * An ad-hoc worker selection: explicit employeeIds OR a filter (or both — the
 * resolver intersects them). At least one must be present.
 */
export const WorkerSelectionSchema = z
  .object({
    employeeIds: z.array(z.string().min(1)).optional(),
    filter: WorkerSelectionFilterSchema.optional(),
  })
  .refine((v) => !!v.employeeIds?.length || !!v.filter, {
    message: 'provide employeeIds or filter',
  });
export type WorkerSelection = z.infer<typeof WorkerSelectionSchema>;

/** Every bulk op returns the count + ids actually affected (post-scope). */
export const BulkResultSchema = z.object({
  affected: z.number().int(),
  employeeIds: z.array(z.string()),
});
export type BulkResult = z.infer<typeof BulkResultSchema>;

// ─── SET ATTRIBUTES ────────────────────────────────────────────────────────────
// Bulk-set position (designationId — the "category") and/or department. Both
// optional but at least one required; each id is validated to exist server-side.
export const BulkSetAttributesSchema = z
  .object({
    selection: WorkerSelectionSchema,
    updates: z
      .object({
        designationId: z.string().min(1).optional(),
        departmentId: z.string().min(1).optional(),
      })
      .refine((u) => u.designationId !== undefined || u.departmentId !== undefined, {
        message: 'provide at least one attribute to update',
      }),
  });
export type BulkSetAttributes = z.infer<typeof BulkSetAttributesSchema>;

// ─── ACTIVATE / DEACTIVATE ─────────────────────────────────────────────────────
// Bulk activate/deactivate by setting employmentStatus. enrollmentStatus is
// system-controlled (flipped by the field/HR enroll flow) and is NOT settable.
export const BulkActivateSchema = z.object({
  selection: WorkerSelectionSchema,
  employmentStatus: z.enum(['ACTIVE', 'TERMINATED']),
});
export type BulkActivate = z.infer<typeof BulkActivateSchema>;
